import React, { useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { logServiceModeSelected } from '@fhs/cart/src/utils/logging-utils';
import { tokens } from '@fhs/ui';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { useCRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { CHECKOUT_SERVICE_MODE_PICKER } from '@fhs-legacy/frontend/src/utils/test-ids';

import { PickupServiceModeOption, ServiceModeConfig } from './pickup-service-mode-option';
import { ServiceModeAccordion } from './service-mode-accordion';
import SingleServiceModeDisplay from './single-service-mode-display';

enum ServiceMode {
  DRIVE_THRU = 'DRIVE_THRU',
  EAT_IN = 'EAT_IN',
  TAKEOUT = 'TAKEOUT',
  CURBSIDE = 'CURBSIDE',
  PICKUP_WINDOW = 'PICKUP_WINDOW',
  DELIVERY = 'DELIVERY',
}

const SERVICE_MODE_CONFIG = {
  [ServiceMode.DRIVE_THRU]: {
    label: 'Drive Thru',
    description: 'Order from your car and pick up at the drive-thru window',
  },
  [ServiceMode.EAT_IN]: {
    label: 'Dine In',
    description: 'Order and enjoy your meal in our restaurant',
  },
  [ServiceMode.TAKEOUT]: {
    label: 'Pick Up',
    description: 'Order online and pick up at the counter',
  },
  [ServiceMode.CURBSIDE]: {
    label: 'Curbside',
    description: "Order online and we'll bring it to your car",
  },
  [ServiceMode.PICKUP_WINDOW]: {
    label: 'Rapid Rescue',
    description: 'Quick pickup at our dedicated window',
  },
  [ServiceMode.DELIVERY]: {
    label: 'Delivery',
    description: "Order online and we'll deliver to your address",
  },
};

const PICKUP_SERVICE_MODES = [
  ServiceMode.TAKEOUT,
  ServiceMode.DRIVE_THRU,
  ServiceMode.CURBSIDE,
  ServiceMode.EAT_IN,
  ServiceMode.PICKUP_WINDOW,
];

export default function ServiceModePicker() {
  const { serviceModeStatus } = useStoreContext();
  const { serviceMode: selectedServiceMode, selectServiceMode } = useOrderContext();
  const { selectServiceMode: selectCartServiceMode } = useCartContext();
  const { logRBIEvent } = useCRMEventsContext();

  const availableServiceModes = useMemo(() => {
    return PICKUP_SERVICE_MODES.filter(serviceMode => {
      return serviceModeStatus[serviceMode]?.available && !serviceModeStatus[serviceMode]?.disabled;
    });
  }, [serviceModeStatus]);

  const isSingleServiceMode = availableServiceModes.length === 1;

  const handleSetServiceMode = useCallback(
    (mode: ServiceMode) => {
      const isUnavailable = serviceModeStatus[mode]?.disabled;
      if (isUnavailable) {
        return;
      }
      // Log service mode selection
      const serviceModeLabel = SERVICE_MODE_CONFIG[mode]?.label || mode;
      logServiceModeSelected(logRBIEvent, serviceModeLabel);

      selectServiceMode(mode);
      // update CartContext local state so tryPricing uses the correct service mode immediately
      selectCartServiceMode(mode);
    },
    [serviceModeStatus, selectServiceMode, selectCartServiceMode, logRBIEvent]
  );

  /*
   * Check currently selected service mode is still available otherwise
   * default to the first available service mode, saves from invalid state
   */
  useEffect(() => {
    if (
      serviceModeStatus[selectedServiceMode]?.available &&
      !serviceModeStatus[selectedServiceMode]?.disabled
    ) {
      return;
    }

    const firstAvailableServiceMode = PICKUP_SERVICE_MODES.find(serviceMode => {
      return serviceModeStatus[serviceMode]?.available && !serviceModeStatus[serviceMode]?.disabled;
    });

    if (firstAvailableServiceMode) {
      selectServiceMode(firstAvailableServiceMode);
      selectCartServiceMode(firstAvailableServiceMode);
    }
  }, [selectedServiceMode, serviceModeStatus, selectServiceMode, selectCartServiceMode]);

  const createServiceModeConfig = (mode: ServiceMode): ServiceModeConfig => ({
    serviceMode: mode,
    header: SERVICE_MODE_CONFIG[mode].label,
    description: SERVICE_MODE_CONFIG[mode].description,
    unavailable: serviceModeStatus[mode]?.disabled || false,
    hidden: !serviceModeStatus[mode]?.available,
  });

  const selectedServiceModeLabel = SERVICE_MODE_CONFIG[selectedServiceMode]?.label;
  const selectedServiceModeDescription = SERVICE_MODE_CONFIG[selectedServiceMode]?.description;

  return (
    <View testID={CHECKOUT_SERVICE_MODE_PICKER}>
      {isSingleServiceMode ? (
        <SingleServiceModeDisplay
          selectedServiceModeLabel={selectedServiceModeLabel}
          selectedServiceModeDescription={selectedServiceModeDescription}
        />
      ) : (
        <ServiceModeAccordion
          selectedServiceModeLabel={selectedServiceModeLabel}
          selectedServiceModeDescription={selectedServiceModeDescription}
        >
          <View
            style={{
              borderBottomWidth: StyleSheet.hairlineWidth,
              borderBottomColor: tokens.colors.$black10,
            }}
          >
            {PICKUP_SERVICE_MODES.map((serviceMode, index) => (
              <PickupServiceModeOption
                key={serviceMode}
                config={createServiceModeConfig(serviceMode)}
                isSelected={selectedServiceMode === serviceMode}
                index={index}
                onSelect={() => handleSetServiceMode(serviceMode)}
              />
            ))}
          </View>
        </ServiceModeAccordion>
      )}
    </View>
  );
}
